// ImportPanel — bulk import students + guardians from Excel/CSV:
// download template → upload → preview with per-row errors → import →
// parent logins (shown once) to download or print.
import { useState } from 'react';
import { Download, Upload, FileSpreadsheet, Printer, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { downloadImportTemplate, previewImport, commitImport } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader, StatTile } from '../ui/Card';
import { Msg, errText, theadRowCls } from '../ui/Form';
import PrintSheet, { PrintHeader } from '../ui/PrintSheet';
import { downloadCsv, fmtDate, todayISO } from '../ui/format';

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('Could not read that file'));
  r.readAsDataURL(file);
});

export default function ImportPanel({ schoolName, onDone }) {
  const [file,    setFile]    = useState(null);   // { name, b64 }
  const [create,  setCreate]  = useState(true);
  const [preview, setPreview] = useState(null);
  const [result,  setResult]  = useState(null);
  const [filter,  setFilter]  = useState('all');
  const [busy,    setBusy]    = useState(false);
  const [msg,     setMsg]     = useState('');
  const [print,   setPrint]   = useState(false);

  async function template() {
    try {
      const blob = await downloadImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'student-import-template.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { setMsg(errText(err)); }
  }

  async function choose(e) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setMsg(''); setPreview(null); setResult(null);
    if (!/\.(xlsx|csv)$/i.test(f.name)) return setMsg('Error: choose an .xlsx (Excel) or .csv file. For old .xls files, open in Excel and "Save as" .xlsx first.');
    try {
      const b64 = await readAsBase64(f);
      setFile({ name: f.name, b64 });
      await runPreview({ name: f.name, b64 }, create);
    } catch (err) { setMsg(errText(err)); }
  }

  async function runPreview(f = file, createClasses = create) {
    setBusy(true); setMsg('');
    try { setPreview(await previewImport({ file: f.b64, filename: f.name, create_classes: createClasses })); setFilter('all'); }
    catch (err) { setPreview(null); setMsg(errText(err)); }
    finally { setBusy(false); }
  }

  async function doImport() {
    if (!window.confirm(`Import ${preview.summary.valid} student${preview.summary.valid === 1 ? '' : 's'}?${preview.summary.invalid ? ` ${preview.summary.invalid} row(s) with errors will be skipped.` : ''}`)) return;
    setBusy(true); setMsg('');
    try {
      const r = await commitImport({ file: file.b64, filename: file.name, create_classes: create });
      setResult(r); setPreview(null); setFile(null);
    } catch (err) { setMsg(errText(err)); }
    finally { setBusy(false); }
  }

  const rows = preview ? preview.rows.filter(r => filter === 'all' || (filter === 'errors' ? r.errors.length : !r.errors.length)) : [];
  const credCols = [
    { key: 'guardian_name', label: 'Parent' }, { key: 'children', label: 'Children' }, { key: 'phone', label: 'Phone' },
    { key: 'login_id', label: 'Login ID' }, { key: 'password', label: 'Password' },
  ];

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-1">Bulk Import Students</h2>
      <p className="text-sm text-ink-soft mb-4">Add a whole school or class at once from an Excel sheet. Nothing is saved until you check the preview and click Import.</p>

      <Card className="p-5 mb-4">
        <ol className="flex flex-col gap-4">
          <li className="flex flex-wrap items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-transit text-white grid place-items-center text-sm font-bold">1</span>
            <span className="text-sm text-ink flex-1 min-w-[220px]">Download the template and fill one row per student (or use your own sheet with the same column names).</span>
            <Button variant="secondary" onClick={template} className="flex items-center gap-1.5"><Download size={14} /> Excel template</Button>
          </li>
          <li className="flex flex-wrap items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-transit text-white grid place-items-center text-sm font-bold">2</span>
            <span className="text-sm text-ink flex-1 min-w-[220px]">Upload the filled file (.xlsx or .csv).{file && <b className="ml-1">{file.name}</b>}</span>
            <label className={`flex items-center gap-1.5 font-bold text-sm px-4 py-2.5 rounded-s cursor-pointer bg-transit text-white hover:bg-transit-deep ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
              <Upload size={14} /> {busy && !preview ? 'Checking…' : 'Upload file'}
              <input type="file" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" className="hidden" onChange={choose} aria-label="Upload student file" />
            </label>
          </li>
          <li className="flex flex-wrap items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-transit text-white grid place-items-center text-sm font-bold">3</span>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink flex-1">
              <input type="checkbox" checked={create} onChange={e => { setCreate(e.target.checked); if (file) runPreview(file, e.target.checked); }} className="accent-[var(--transit)]" />
              Create classes that don't exist yet
            </label>
          </li>
        </ol>
      </Card>

      <Msg msg={msg} className="mb-3" />

      {preview && (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3.5 mb-4">
            <StatTile tone="transit" icon={FileSpreadsheet} label="Rows in file" value={preview.summary.total} />
            <StatTile tone="success" icon={CheckCircle2} label="Ready to import" value={preview.summary.valid} />
            <StatTile tone={preview.summary.invalid ? 'critical' : 'slate'} icon={AlertTriangle} label="With errors" value={preview.summary.invalid} caption={preview.summary.invalid ? 'skipped — fix and re-upload' : null} />
            <StatTile tone="clearance" label="New parent logins" value={preview.summary.new_guardians} caption={`${preview.summary.existing_guardians} existing parents linked`} />
          </div>
          {preview.summary.new_classes.length > 0 && (
            <p className="text-sm font-semibold text-transit-deep bg-transit-tint px-3 py-2 rounded-s w-fit mb-3">New classes: {preview.summary.new_classes.join(', ')}</p>
          )}
          <Card>
            <CardHeader title="Preview" action={
              <div className="flex items-center gap-2">
                <select className="border border-line rounded-s px-2 py-1.5 text-sm bg-paper-raised" value={filter} onChange={e => setFilter(e.target.value)} aria-label="Show rows">
                  <option value="all">All rows</option><option value="errors">Only errors</option><option value="ok">Only ready</option>
                </select>
                <Button onClick={doImport} disabled={busy || !preview.summary.valid}>{busy ? 'Importing…' : `Import ${preview.summary.valid} student${preview.summary.valid === 1 ? '' : 's'}`}</Button>
              </div>
            } />
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0"><tr className={theadRowCls}><th className="px-3 py-2">Row</th><th className="px-3 py-2">Student</th><th className="px-3 py-2">Class</th><th className="px-3 py-2">Parent</th><th className="px-3 py-2">Check</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.row_no} className={`border-t border-line-soft align-top ${r.errors.length ? 'bg-critical-tint' : ''}`}>
                      <td className="px-3 py-2 tabular-nums text-ink-soft">{r.row_no}</td>
                      <td className="px-3 py-2 font-semibold">{r.name || '—'}<span className="block text-xs text-ink-soft font-normal">{[r.admission_no && `Adm ${r.admission_no}`, r.roll_number && `Roll ${r.roll_number}`, r.date_of_birth && `DOB ${fmtDate(r.date_of_birth)}`].filter(Boolean).join(' · ')}</span></td>
                      <td className="px-3 py-2">{r.class_name || '—'}</td>
                      <td className="px-3 py-2">{r.guardian?.name || '—'}<span className="block text-xs text-ink-soft">{r.guardian_phone}{r.guardian ? (r.guardian.existing ? ' · already registered' : ' · new login') : ''}</span></td>
                      <td className="px-3 py-2">
                        {!r.errors.length && <Badge tone="success">OK</Badge>}
                        {r.errors.map(e => <span key={e} className="block text-xs font-bold text-critical">{e}</span>)}
                        {r.warnings.map(w => <span key={w} className="block text-xs text-caution">{w}</span>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {result && (
        <Card className="p-5">
          <h3 className="font-display font-bold text-lg text-success mb-2">Imported {result.created} student{result.created === 1 ? '' : 's'} ✅</h3>
          <p className="text-sm text-ink mb-3">
            {result.guardians_created} new parent login{result.guardians_created === 1 ? '' : 's'} · {result.guardians_linked} linked to existing parents
            {result.classes_created.length ? ` · classes created: ${result.classes_created.join(', ')}` : ''}
            {result.skipped ? ` · ${result.skipped} row(s) skipped because of errors` : ''}
          </p>
          {result.credentials.length > 0 && (
            <>
              <p className="flex items-center gap-2 text-sm font-semibold text-caution bg-caution-tint px-3 py-2 rounded-s w-fit mb-3">
                <AlertTriangle size={15} /> Save or print these parent logins now — the passwords are only shown this once.
              </p>
              <div className="flex gap-2 mb-3">
                <Button onClick={() => downloadCsv(`parent-logins-${todayISO()}.csv`, credCols, result.credentials)} className="flex items-center gap-1.5"><Download size={14} /> Download logins (CSV)</Button>
                <Button variant="secondary" onClick={() => setPrint(true)} className="flex items-center gap-1.5"><Printer size={14} /> Print login slips</Button>
              </div>
              <div className="overflow-x-auto max-h-[40vh] overflow-y-auto border border-line rounded-s">
                <table className="w-full text-sm">
                  <thead><tr className={theadRowCls}>{credCols.map(c => <th key={c.key} className="px-3 py-2">{c.label}</th>)}</tr></thead>
                  <tbody>{result.credentials.map(c => (
                    <tr key={c.phone} className="border-t border-line-soft">{credCols.map(k => <td key={k.key} className={`px-3 py-2 ${k.key === 'password' ? 'font-mono font-bold' : ''}`}>{c[k.key]}</td>)}</tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
          <Button variant="secondary" className="mt-4" onClick={onDone}>Go to Students</Button>
        </Card>
      )}

      {print && result && (
        <PrintSheet title="Parent login slips" onClose={() => setPrint(false)}>
          <div className="grid grid-cols-2 gap-4">
            {result.credentials.map(c => (
              <div key={c.phone} className="border border-dashed border-[#9aa8a6] rounded-s p-4 text-[13px]" style={{ breakInside: 'avoid' }}>
                <PrintHeader schoolName={schoolName} docTitle="Parent app login" />
                <p>Dear <b>{c.guardian_name}</b> (parent of {c.children}),</p>
                <p className="mt-2">Install the school's parent app and log in with:</p>
                <p className="mt-2">Login ID: <b className="font-mono">{c.login_id}</b><br />Password: <b className="font-mono">{c.password}</b></p>
                <p className="mt-2 text-[11.5px] text-[#405058]">You'll also need the school code from the office. Keep this slip private.</p>
              </div>
            ))}
          </div>
        </PrintSheet>
      )}
    </div>
  );
}
